import styled from '@emotion/styled';
import StatementChart from './StatementChart';
import { BodySmall, theme } from '@arnold/common';
import { ContentBox } from './ContentBox';
import { ReportWidgetProps } from './ReportWidget/types';
import { getWidgetTitle, isGraphRemovable } from './ReportWidget/utils';
import { IReport } from '../../lib/reports/base';
import { getGraphLabel } from '../../lib/reportHelper';
// eslint-disable-next-line id-blacklist
import { any } from 'ramda';
import { useTranslation } from 'react-i18next';
import { getTeamsForReversedGraph } from '../../lib/reports/queryAggregatedData';
import { AggregatedData } from '../../lib/reports/aggregateData';
import { processStatements, Statement } from '../../lib/reports/statementHelpers';

export type ChartOption = {
  optionName: string;
  highLightLevel: number;
  index: number;
};

type ScaleQuestionWidgetProps = ReportWidgetProps & {
  label?: string;
  report: IReport;
  aggregatedData: AggregatedData | null;
};

export const getAColorByHighlightLevel = (
  highlightLevel: number,
  numberOfItemsWithThisHighlightLevel: number,
  index: number,
) => {
  if (numberOfItemsWithThisHighlightLevel === 1 || highlightLevel !== 0) {
    // For highlight level 1 or 2 or highlight level 0 with only one item with this highlight level
    // return the color for the highlight level
    return highlightLevel === 0
      ? theme.colors.chart.neutral[1]
      : highlightLevel === 1
        ? theme.colors.chart.warning[0]
        : theme.colors.chart.alert[0];
  }
  // For highlight level 0 with more than one item with this highlight level
  // We will have to interpolate the colors from light blue to dark blue
  const colorFrom = theme.colors.chart.neutral[1];
  const colorTo = theme.colors.chart.neutral[3];
  const fraction = index / (numberOfItemsWithThisHighlightLevel - 1);
  const color = interpolateColor(colorFrom, colorTo, fraction);
  return color;
};

// Color should be in hex format #RRGGBB
const interpolateColor = (colorFrom: string, colorTo: string, fraction: number) => {
  if (fraction < 0 || fraction > 1) {
    throw new Error('Fraction must be between 0 and 1. Fraction: ' + fraction);
  }
  if (colorFrom.length !== 7 || colorTo.length !== 7) {
    throw new Error('Colors must be in hex format #RRGGBB');
  }
  const rFrom = parseInt(colorFrom.slice(1, 3), 16);
  const gFrom = parseInt(colorFrom.slice(3, 5), 16);
  const bFrom = parseInt(colorFrom.slice(5, 7), 16);
  const rTo = parseInt(colorTo.slice(1, 3), 16);
  const gTo = parseInt(colorTo.slice(3, 5), 16);
  const bTo = parseInt(colorTo.slice(5, 7), 16);
  const r = Math.round(rFrom + (rTo - rFrom) * fraction);
  const g = Math.round(gFrom + (gTo - gFrom) * fraction);
  const b = Math.round(bFrom + (bTo - bFrom) * fraction);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const ScaleQuestionWidget = ({
  widget,
  report,
  hideHeader,
  globalFilters,
  loading,
  i,
  aggregatedData,
  filters,
  ...props
}: ScaleQuestionWidgetProps) => {
  const { t } = useTranslation('Report');

  if (!report.questions || !aggregatedData) {
    return null;
  }
  const questionIndex = widget.questionDefinitionIndex;
  const question = report.questions.find((question) => question.index === questionIndex);
  if (!question) {
    return null;
  }
  const statementsLocal = question.statementsLocal;
  if (!statementsLocal) {
    return null;
  }
  const chartOptions: ChartOption[] =
    question.answersLocal?.map((option, index) => ({
      optionName: option || '',
      highLightLevel: 0,
      index,
    })) || [];

  question.answersToHighlight?.forEach((highlightLvlIndex: string) => {
    chartOptions[parseInt(highlightLvlIndex, 10)].highLightLevel++;
  });

  const statements = processStatements(statementsLocal, questionIndex || '', report, t('ANONYMOUS'));

  const traversable = report.teams?.length !== 1 && !any((df) => df.operator === 'team', widget.displayFilters || []);
  const label = getGraphLabel(report, widget, widget.questionDefinitionIndex || '', traversable, t).label;
  const removable = isGraphRemovable(widget.id, props.addedWidgets);

  const sortedChartOptions = [...chartOptions].sort((a, b) => b.highLightLevel - a.highLightLevel);

  if (statements.every((statement) => statement.answers.length === 0 && statement.comments.length === 0)) {
    return null;
  }

  const compareLevelFilter = (globalFilters || []).find((filter) => filter.operator === 'organization-level');
  const teams = getTeamsForReversedGraph(aggregatedData, report, compareLevelFilter);

  // Group statements into chunks of 4 for powerpoint export
  const STATEMENTS_PER_PAGE = 4;
  const statementGroups: Statement[][] = [];

  for (let i = 0; i < statements.length; i += STATEMENTS_PER_PAGE) {
    statementGroups.push(statements.slice(i, i + STATEMENTS_PER_PAGE));
  }

  return (
    <div className={'mt-7'} data-icom="report-scale-question-widget">
      <ContentBox
        disabled={false}
        header={hideHeader || removable ? undefined : getWidgetTitle(report, widget)}
        questionDefinitionIndex={widget.questionDefinitionIndex || ''}
        {...props}
      >
        <StatementsContainer>
          {label && <div>{label}</div>}
          {!compareLevelFilter && (
            <div>
              <ChartLegend sortedChartOptions={sortedChartOptions} />
            </div>
          )}
          {/* Render each group of statements as a separate page for powerpoint export */}
          {statementGroups.map((statementGroup, pageIndex) => (
            <div
              key={`statement-group-${pageIndex}`}
              data-icom="report-scale-question-widget-statements-needed-part"
              data-for-query-selector-scale-chart
              data-scale-chart-page={pageIndex + 1}
              data-scale-chart-total-pages={statementGroups.length}
              data-scale-chart-question-text={getWidgetTitle(report, widget)}
            >
              <StatementsContainer>
                {/* Legend for PowerPoint - hidden in report but shown in PowerPoint for powerpoint export */}
                <div className="powerpoint-legend" data-pptx-only>
                  <ChartLegend sortedChartOptions={sortedChartOptions} />
                </div>

                {statementGroup.map((statement, index) => (
                  <StatementChart
                    key={`statement-${pageIndex}-${index}`}
                    statement={
                      compareLevelFilter
                        ? {
                            ...statement,
                            comments: statement.comments,
                          }
                        : statement
                    }
                    chartOptions={chartOptions}
                    showEscapeOption={question.showEscapeOption ?? true}
                    showComments={!question.isRankingScale}
                    teams={compareLevelFilter ? teams : undefined}
                    compareLevelFilter={compareLevelFilter}
                  />
                ))}
              </StatementsContainer>
            </div>
          ))}
        </StatementsContainer>
      </ContentBox>
    </div>
  );
};

const ChartLegend = ({ sortedChartOptions }: { sortedChartOptions: ChartOption[] }) => (
  <StatementsHeader>
    <StatementsHeaderContent>
      {sortedChartOptions.map((option) => {
        const optionsWithThisHighlightLevel = sortedChartOptions.filter(
          (o) => o.highLightLevel === option.highLightLevel,
        );
        return (
          <OptionItem key={option.optionName}>
            <ColorCircle
              highlightColor={getAColorByHighlightLevel(
                option.highLightLevel,
                optionsWithThisHighlightLevel.length,
                optionsWithThisHighlightLevel.indexOf(option),
              )}
            />
            <OptionName>{option.optionName}</OptionName>
          </OptionItem>
        );
      })}
    </StatementsHeaderContent>
  </StatementsHeader>
);
const StatementsContainer = styled.div`
  background-color: ${theme.colors.backgroundCover.default};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.g};
  border-radius: ${theme.borderRadius.l};

  .powerpoint-legend {
    display: none;
  }
`;

export const StatementsHeader = styled(BodySmall)<{ width?: string }>`
  color: ${theme.colors.text.primary};
  padding: ${theme.spacing.d} 0;
  display: flex;
  justify-content: flex-end;
  width: ${({ width }) => width || '100%'};
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const StatementsHeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${theme.spacing.g};
`;

export const OptionItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.c};
`;

export const ColorCircle = styled.div<{ highlightColor: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ highlightColor }) => highlightColor};
  border-radius: 50%;
`;

export const OptionName = styled(BodySmall)`
  color: ${theme.colors.text.primary};
`;

export default ScaleQuestionWidget;
